import { FC, ReactNode } from "react";

import { IsoLocale, getLocalesFromIsoLocale, CanonicalTagsConfig, UsaIsoLocale } from "@simplyk/common";
import NextHead from "next/head";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";

import i18n from "../../config/i18n";
import { isProduction } from "../../constants/env";
import { favicon } from "../../constants/favicon";
import { useHeadDescription } from "../../hooks/useHeadDescription";
import { palette } from "../../theme/palette";

import { CanonicalTags } from "./CanonicalTags";

export interface ZeffyHeadProps {
  description?: string;
  disableMobileAutoZoom?: boolean;
  image?: string;
  imageWidth?: string;
  themeColor?: string;
  title?: string;
  video?: string;
  children?: ReactNode;
  noIndex?: boolean;
  metaTitle?: string;
  metaDescription?: string;
  canonicalTags?: CanonicalTagsConfig;
}

export const ZeffyHead: FC<ZeffyHeadProps> = ({
  description: unparsedDescription = "",
  disableMobileAutoZoom = true,
  image,
  imageWidth = "600px",
  themeColor = palette.primary.main,
  title: inputTitle = "Zeffy",
  video,
  children,
  noIndex = false,
  metaTitle,
  metaDescription,
  canonicalTags,
}) => {
  // Robust Fallback in case an falsy value is passed as a title
  const currentTitle = inputTitle ? inputTitle : "Zeffy";
  const router = useRouter();

  const pathname = usePathname();
  const isoLocale = (i18n?.language || router.locale || UsaIsoLocale.EN) as IsoLocale;
  const language = getLocalesFromIsoLocale(isoLocale);

  const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL as string;
  const fallbackCanonicalUrl = `${frontendUrl}${pathname}`;
  const mainCanonicalUrl = canonicalTags?.url ?? fallbackCanonicalUrl;

  const parsedDescription = useHeadDescription(unparsedDescription);
  const description = parsedDescription ? parsedDescription : currentTitle;

  const viewPortContent = disableMobileAutoZoom
    ? "width=device-width, initial-scale=1, maximum-scale=1"
    : "width=device-width, initial-scale=1";

  return (
    <NextHead>
      {(!isProduction || noIndex) && <meta name="robots" content="noindex, nofollow" />}
      <title>{currentTitle}</title>
      <link rel="icon" href={favicon} />
      <link rel="manifest" href="/manifest.json" />

      <meta charSet="utf-8" />

      <meta name="author" content="Zeffy" />
      <meta name="copyright" content="Zeffy" />
      <meta name="description" content={metaDescription || description} />
      <meta name="language" content={language} />
      <meta name="theme-color" content={themeColor} />
      <meta name="title" content={metaTitle || currentTitle} />
      <meta name="viewport" content={viewPortContent} />

      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="Zeffy" />
      <meta property="og:title" content={currentTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={mainCanonicalUrl} />
      {image && (
        <>
          <meta property="og:image" content={image} />
          <meta property="og:image:secure_url" content={image} />
          <meta property="og:image:width" content={imageWidth} />
        </>
      )}
      {video && <meta property="og:video" content={video} />}

      <CanonicalTags
        url={mainCanonicalUrl}
        default={canonicalTags?.default ?? mainCanonicalUrl}
        alternate={canonicalTags?.alternate}
      />
      {children}
    </NextHead>
  );
};
